import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react'
import {
  Box,
  Typography,
  AppBar,
  Toolbar,
  Chip,
  CircularProgress,
  Button,
  LinearProgress,
  IconButton,
  TextField,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { collection, doc, getDoc, getDocs, orderBy, query, where } from 'firebase/firestore'
import { useCollection, useCollectionData, useDocument } from 'react-firebase-hooks/firestore'
import { firebase_firestore } from '../../../common/firebaseConfig'
import { callableRef } from '../../../api/callableRef'
import { useApplicationSuiteContext } from '../../../contexts/ApplicationSuiteProvider'
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import SendIcon from '@mui/icons-material/Send'
import StopIcon from '@mui/icons-material/Stop'
import LoopIcon from '@mui/icons-material/Loop'
import AccountTreeIcon from '@mui/icons-material/AccountTree'
import AssessmentIcon from '@mui/icons-material/Assessment'
import Tooltip from '@mui/material/Tooltip'
import ReportCardList from '../../../components/backtests/ReportCardList'
import Application from '../../../layout/Application'
import LaunchAgentDialog from '../../../components/agent/LaunchAgentDialog'
import { Tabs } from '../../../layout/MainLayout'
import { captureEvent } from '../../../posthog'

interface AgentSession {
  sessionId: string
  patterns: any[]
  backtests: any[]
  latestTime: number
}

function SessionsContent() {
  const theme = useTheme()
  const { usermeta, backtestToView, setBacktestToView, setActiveTab } = useApplicationSuiteContext()

  useEffect(() => {
    if (backtestToView) {
      setActiveTab({ tab: Tabs.Showcase })
    }
  }, [backtestToView]) // eslint-disable-line react-hooks/exhaustive-deps

  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null)
  const [highlightedBacktestId, setHighlightedBacktestId] = useState<string | null>(null)
  const [isLaunchDialogOpen, setIsLaunchDialogOpen] = useState(false)

  // Auto-clear the highlight after a short time so the border flashes then fades
  useEffect(() => {
    if (!highlightedBacktestId) return
    const t = setTimeout(() => setHighlightedBacktestId(null), 1800)
    return () => clearTimeout(t)
  }, [highlightedBacktestId])

  const [agentPatterns, patternsLoading] = useCollectionData(
    query(
      collection(firebase_firestore, `usermeta/${usermeta.gcid}/patterns`),
      where('source', '==', 'strategy-agent')
    )
  )

  const [agentBacktests, backtestsLoading] = useCollectionData(
    query(
      collection(firebase_firestore, `usermeta/${usermeta.gcid}/backtests`),
      where('source', '==', 'strategy-agent')
    )
  )

  const [runDocsSnap] = useCollection(
    collection(firebase_firestore, `usermeta/${usermeta.gcid}/strategyAgentRuns`)
  )
  const runDataMap = useMemo(() => {
    const map: Record<string, { currentIteration?: number; maxIterations?: number }> = {}
    runDocsSnap?.docs.forEach((d) => {
      map[d.id] = d.data()
    })
    return map
  }, [runDocsSnap])

  const [runningSessionsSnap] = useCollection(
    query(
      collection(firebase_firestore, `usermeta/${usermeta.gcid}/strategyAgentRuns`),
      where('status', '==', 'running')
    )
  )
  const runningSessions = useMemo(
    () => runningSessionsSnap?.docs.map((d) => ({ ...d.data(), sessionId: d.id })),
    [runningSessionsSnap]
  )

  const loading = patternsLoading || backtestsLoading

  const sessions = useMemo<AgentSession[]>(() => {
    const map: Record<string, AgentSession> = {}

    agentPatterns?.forEach((pattern) => {
      const sid = pattern.agentSessionId || 'unknown'
      if (!map[sid]) map[sid] = { sessionId: sid, patterns: [], backtests: [], latestTime: 0 }
      map[sid].patterns.push(pattern)
    })

    agentBacktests?.forEach((backtest) => {
      const sid = backtest.agentSessionId || 'unknown'
      if (!map[sid]) map[sid] = { sessionId: sid, patterns: [], backtests: [], latestTime: 0 }
      map[sid].backtests.push(backtest)
      const t = backtest.startTime || 0
      if (t > map[sid].latestTime) map[sid].latestTime = t
    })

    return Object.values(map).sort((a, b) => b.latestTime - a.latestTime)
  }, [agentPatterns, agentBacktests])

  // A session with no saved pattern or backtest yet (still running its first steps) has no
  // entry in `sessions`, which is built only from patterns/backtests. Falling back to
  // sessions[0] in that case silently re-selects a different session instead of the one just
  // clicked. Synthesize an empty shell instead — but only while the session is still actually
  // running; a session that finished without ever producing an artifact (e.g. errored out
  // immediately) should fall through to sessions[0] rather than getting stuck displayed as an
  // empty shell forever.
  const selectedSession = selectedSessionId
    ? (sessions.find((s) => s.sessionId === selectedSessionId) ??
      (runningSessions?.some((r) => r.sessionId === selectedSessionId)
        ? { sessionId: selectedSessionId, patterns: [], backtests: [], latestTime: 0 }
        : sessions[0] || null))
    : sessions[0] || null

  const effectiveSessionId = selectedSession?.sessionId ?? null

  const [selectedRunDoc] = useDocument(
    effectiveSessionId
      ? doc(firebase_firestore, `usermeta/${usermeta.gcid}/strategyAgentRuns/${effectiveSessionId}`)
      : null
  )

  const [steps] = useCollectionData(
    effectiveSessionId
      ? query(
          collection(
            firebase_firestore,
            `usermeta/${usermeta.gcid}/strategyAgentRuns/${effectiveSessionId}/steps`
          ),
          orderBy('step', 'asc')
        )
      : null
  )

  const [backtestSummaries, setBacktestSummaries] = useState<Record<string, any[]>>({})

  const backtestStatusKey =
    selectedSession?.backtests.map((bt) => `${bt.id}:${bt.status}`).join(',') ?? ''

  useEffect(() => {
    if (!selectedSession) return
    const backtestsToFetch = selectedSession.backtests.filter(
      (bt) =>
        bt.id && ['complete', 'archived'].includes(bt.status) && !backtestSummaries[bt.id]?.length
    )
    if (!backtestsToFetch.length) return

    Promise.all(
      backtestsToFetch.map(async (backtest) => {
        const snap = await getDocs(
          collection(
            firebase_firestore,
            `usermeta/${usermeta.gcid}/backtests/${backtest.id}/summaries`
          )
        )
        return { id: backtest.id, summaries: snap.docs.map((d) => d.data()) }
      })
    ).then((results) => {
      setBacktestSummaries((prev) => {
        const next = { ...prev }
        results.forEach(({ id, summaries }) => {
          next[id] = summaries
        })
        return next
      })
    })
  }, [selectedSession?.sessionId, backtestStatusKey, usermeta.gcid]) // eslint-disable-line react-hooks/exhaustive-deps

  const formatDate = (timestamp: number) => {
    if (!timestamp) return '—'
    return new Date(timestamp).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  const getBestProfitFactor = (session: AgentSession): number | null => {
    let best = 0
    session.backtests.forEach((bt) => {
      bt.summaries?.forEach((s: any) => {
        if ((s.profitFactor || 0) > best) best = s.profitFactor
      })
    })
    return best > 0 ? best : null
  }

  const getSessionSymbol = (session: AgentSession): string | null => {
    const bt = session.backtests[0]
    if (!bt) return null
    const symbol = bt.backtestSettings?.executionSymbol
    const periodicity = bt.backtestSettings?.periodicity
    const timeframe = bt.backtestSettings?.timeframe
    if (!symbol) return null
    const tfLabel = periodicity
      ? ` ${periodicity}${timeframe ? timeframe.charAt(0).toUpperCase() : ''}`
      : ''
    return `${symbol}${tfLabel}`
  }

  const getPatternName = (session: AgentSession): string => {
    if (session.patterns.length > 0) return session.patterns[0].name
    if (session.backtests.length > 0)
      return session.backtests[0].backtestSettings?.name || 'Unnamed'
    return 'Unnamed Session'
  }

  const hasRunningBacktest = (session: AgentSession) =>
    session.backtests.some((bt) => bt.status === 'running')

  const hasRunningAgentSession = (session: AgentSession) =>
    runningSessions?.some((r) => r.sessionId === session.sessionId) ?? false

  return (
    <Box
      sx={{ display: 'flex', height: '100%', overflow: 'hidden', flexDirection: 'column' }}
      px={1}
    >
      <AppBar
        position="static"
        sx={{
          boxShadow: 'none',
          background: 'none',
          borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
          mb: 1,
        }}
      >
        <Toolbar style={{ padding: 0, minHeight: 56, gap: 8 }}>
          <Box display="flex" alignItems="center" gap={1} flexGrow={1}>
            <SmartToyOutlinedIcon sx={{ color: theme.palette.primary.main }} />
            <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: '-0.02em' }}>
              AI Sessions
            </Typography>
            {loading && <CircularProgress size={16} thickness={5} />}
          </Box>
          {usermeta.isAdmin && (
            <Button
              variant="contained"
              size="small"
              startIcon={<SmartToyOutlinedIcon />}
              onClick={() => setIsLaunchDialogOpen(true)}
              sx={{ flexShrink: 0 }}
            >
              New Session
            </Button>
          )}
        </Toolbar>
      </AppBar>

      {!loading && sessions.length === 0 && (!runningSessions || runningSessions.length === 0) ? (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          height="100%"
          gap={1.5}
        >
          <SmartToyOutlinedIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
          <Typography variant="body1" color="text.secondary" fontWeight={500}>
            No AI sessions yet
          </Typography>
          <Typography variant="body2" color="text.disabled" textAlign="center">
            Run the strategy agent to see sessions appear here
          </Typography>
          {usermeta.isAdmin && (
            <Button
              variant="outlined"
              startIcon={<SmartToyOutlinedIcon />}
              onClick={() => setIsLaunchDialogOpen(true)}
              sx={{ mt: 1 }}
            >
              New Session
            </Button>
          )}
        </Box>
      ) : (
        <Box display="flex" gap={1} height="100%" overflow="hidden">
          {/* Session list */}
          <Box
            sx={{
              width: 220,
              flexShrink: 0,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              pr: 1,
              borderRight: `1px solid ${theme.palette.divider}`,
            }}
          >
            {/* Running sessions from strategyAgentRuns */}
            {runningSessions?.map((run) => (
              <RunningSessionCard
                key={run.sessionId}
                run={run}
                isSelected={effectiveSessionId === run.sessionId}
                onClick={() => setSelectedSessionId(run.sessionId)}
              />
            ))}

            {/* Completed sessions from artifacts */}
            {sessions.map((session) => {
              const isRunningRun = runningSessions?.some((r) => r.sessionId === session.sessionId)
              if (isRunningRun) return null // already shown above

              const isSelected = effectiveSessionId === session.sessionId
              const pf = getBestProfitFactor(session)
              const running = hasRunningBacktest(session) || hasRunningAgentSession(session)
              const runData = runDataMap[session.sessionId]

              return (
                <Box
                  key={session.sessionId}
                  onClick={() => setSelectedSessionId(session.sessionId)}
                  sx={{
                    p: 1.5,
                    borderRadius: 1,
                    cursor: 'pointer',
                    border: `1px solid ${
                      isSelected ? theme.palette.primary.main : 'rgba(255,255,255,0.06)'
                    }`,
                    backgroundColor: isSelected
                      ? `${theme.palette.primary.main}18`
                      : theme.palette.background.custom3,
                    transition: 'all 0.15s ease',
                    '&:hover': {
                      backgroundColor: isSelected
                        ? `${theme.palette.primary.main}22`
                        : theme.palette.action.hover,
                    },
                  }}
                >
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                    <Box display="flex" alignItems="center" gap={0.75}>
                      <SmartToyOutlinedIcon
                        sx={{
                          fontSize: 13,
                          color: isSelected
                            ? theme.palette.primary.main
                            : theme.palette.text.secondary,
                        }}
                      />
                      {running && (
                        <Box
                          sx={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            backgroundColor: theme.palette.info.main,
                            animation: 'agentPulse 1.5s ease-in-out infinite',
                            '@keyframes agentPulse': {
                              '0%, 100%': { opacity: 1 },
                              '50%': { opacity: 0.25 },
                            },
                          }}
                        />
                      )}
                    </Box>
                    <Typography
                      variant="caption"
                      color="text.disabled"
                      sx={{ fontSize: '0.65rem' }}
                    >
                      {formatDate(session.latestTime)}
                    </Typography>
                  </Box>

                  <Typography
                    variant="body2"
                    color={isSelected ? 'text.primary' : 'text.secondary'}
                    fontWeight={isSelected ? 600 : 400}
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      mb: 0.75,
                      fontSize: '0.8rem',
                    }}
                  >
                    {getPatternName(session)}
                  </Typography>

                  <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
                    {getSessionSymbol(session) && (
                      <Chip
                        label={getSessionSymbol(session)}
                        size="small"
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.75 } }}
                      />
                    )}
                    <Chip
                      icon={<AssessmentIcon sx={{ fontSize: '10px !important' }} />}
                      label={`${session.backtests.length}`}
                      size="small"
                      variant="outlined"
                      sx={{ height: 20, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.75 } }}
                    />
                    {runData?.currentIteration != null && runData?.maxIterations != null && (
                      <Chip
                        icon={<LoopIcon sx={{ fontSize: '10px !important' }} />}
                        label={`${runData.currentIteration}/${runData.maxIterations}`}
                        size="small"
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.75 } }}
                      />
                    )}
                    {pf !== null && (
                      <Chip
                        label={`PF ${pf.toFixed(2)}`}
                        size="small"
                        color="success"
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.75 } }}
                      />
                    )}
                  </Box>
                </Box>
              )
            })}
          </Box>

          {/* Middle: agent reasoning */}
          {selectedSession && (
            <AgentReasoningPanel
              session={selectedSession}
              runDoc={selectedRunDoc?.data()}
              steps={steps}
              runningSessions={runningSessions}
              gcid={usermeta.gcid}
              getSessionSymbol={getSessionSymbol}
              getPatternName={getPatternName}
              onBacktestHighlight={setHighlightedBacktestId}
            />
          )}

          {/* Right: backtest cards at fixed 400px */}
          {selectedSession && (
            <Box
              sx={{
                width: 400,
                flexShrink: 0,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                borderLeft: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ fontSize: '0.6rem', letterSpacing: '0.1em', flexShrink: 0, pl: 1, pt: 0.5 }}
              >
                Backtests ({selectedSession.backtests.length})
              </Typography>
              <Box sx={{ flex: 1, minHeight: 0 }}>
                {(() => {
                  const sortedBacktests = selectedSession.backtests
                    .slice()
                    .sort((a, b) => (b.startTime || 0) - (a.startTime || 0))
                    .map((bt) => ({
                      ...bt,
                      summaries: backtestSummaries[bt.id] ?? bt.summaries,
                    }))
                  const highlightedBacktest = highlightedBacktestId
                    ? (sortedBacktests.find((bt) => bt.id === highlightedBacktestId) ?? null)
                    : null
                  return (
                    <ReportCardList
                      backtests={sortedBacktests}
                      activeBacktest={highlightedBacktest}
                      onBacktestSelection={(backtest) => backtest && setBacktestToView(backtest)}
                      disableGrouping
                    />
                  )
                })()}
              </Box>
            </Box>
          )}
        </Box>
      )}

      <LaunchAgentDialog open={isLaunchDialogOpen} onClose={() => setIsLaunchDialogOpen(false)} />
    </Box>
  )
}

function AgentReasoningPanel({
  session,
  runDoc,
  steps,
  runningSessions,
  gcid,
  getSessionSymbol,
  getPatternName,
  onBacktestHighlight,
}: {
  session: AgentSession
  runDoc: any
  steps: any[] | undefined
  runningSessions: any[] | undefined
  gcid: string
  getSessionSymbol: (s: AgentSession) => string | null
  getPatternName: (s: AgentSession) => string
  onBacktestHighlight: (id: string) => void
}) {
  const theme = useTheme()
  const isRunning = runningSessions?.some((r) => r.sessionId === session.sessionId) ?? false
  const lastAction: string | null = runDoc?.lastAction ?? null
  const currentIteration: number = runDoc?.currentIteration ?? 0
  const maxIterations: number = runDoc?.maxIterations ?? 0
  const progress = maxIterations > 0 ? Math.round((currentIteration / maxIterations) * 100) : 0

  // Auto-scroll
  const scrollRef = useRef<HTMLDivElement>(null)
  const bottomRef = useRef<HTMLDivElement>(null)
  const isNearBottomRef = useRef(true)

  const handleScroll = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    isNearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 100
  }, [])

  useEffect(() => {
    if (isNearBottomRef.current) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [steps?.length])

  // Cancel
  const [isCancelling, setIsCancelling] = useState(false)

  const handleCancel = async () => {
    if (isCancelling) return
    setIsCancelling(true)
    try {
      const fn = callableRef('cancelstrategyagentsession')
      await fn({ sessionId: session.sessionId })
      captureEvent('agent_session_cancelled')
    } catch (err) {
      console.error('Failed to cancel session:', err)
      setIsCancelling(false)
    }
    // isCancelling stays true until isRunning goes false (session cancelled)
  }

  // Reset cancelling state if session is no longer running
  useEffect(() => {
    if (!isRunning) setIsCancelling(false)
  }, [isRunning])

  // Message input
  const [messageInput, setMessageInput] = useState('')
  const [isSending, setIsSending] = useState(false)
  const [messageSent, setMessageSent] = useState(false)

  const handleSendMessage = async () => {
    const text = messageInput.trim()
    if (!text || isSending) return
    setIsSending(true)
    setMessageInput('')
    try {
      const fn = callableRef('sendstrategyagentmessage')
      await fn({ sessionId: session.sessionId, message: text })
      captureEvent('agent_session_message_sent')
      setMessageSent(true)
      setTimeout(() => setMessageSent(false), 4000)
    } catch (err) {
      console.error('Failed to send message:', err)
      setMessageInput(text)
    } finally {
      setIsSending(false)
    }
  }

  // Hide "sent" indicator when a user_message step appears
  const stepCount = steps?.length ?? 0
  useEffect(() => {
    if (messageSent) setMessageSent(false)
  }, [stepCount]) // eslint-disable-line react-hooks/exhaustive-deps

  const hasSteps = steps && steps.length > 0
  const fallbackResult: string | null = runDoc?.result ?? null

  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        px: 1,
      }}
    >
      {/* Session header */}
      <Box
        sx={{
          p: 1.5,
          borderRadius: 1,
          backgroundColor: theme.palette.background.custom3,
          border: `1px solid rgba(255,255,255,0.06)`,
          flexShrink: 0,
          mb: 1,
        }}
      >
        <Box display="flex" alignItems="center" gap={0.75} mb={0.5}>
          <SmartToyOutlinedIcon sx={{ fontSize: 14, color: theme.palette.primary.main }} />
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ fontSize: '0.6rem', letterSpacing: '0.1em' }}
          >
            Session
          </Typography>
          {isRunning && (
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                backgroundColor: theme.palette.info.main,
                animation: 'agentPulse 1.5s ease-in-out infinite',
                '@keyframes agentPulse': {
                  '0%, 100%': { opacity: 1 },
                  '50%': { opacity: 0.25 },
                },
              }}
            />
          )}
          {isRunning && (
            <Button
              size="small"
              color="error"
              variant="outlined"
              startIcon={
                isCancelling ? (
                  <CircularProgress size={10} color="error" />
                ) : (
                  <StopIcon sx={{ fontSize: '14px !important' }} />
                )
              }
              onClick={handleCancel}
              disabled={isCancelling}
              sx={{
                ml: 'auto',
                height: 22,
                fontSize: '0.6rem',
                py: 0,
                px: 1,
                minWidth: 0,
                '& .MuiButton-startIcon': { mr: 0.5 },
              }}
            >
              {isCancelling ? 'Stopping…' : 'Stop'}
            </Button>
          )}
        </Box>
        <Typography variant="body1" fontWeight={600} color="text.primary">
          {getPatternName(session)}
        </Typography>
        {getSessionSymbol(session) && (
          <Typography variant="body2" color="text.secondary" mt={0.25}>
            {getSessionSymbol(session)}
          </Typography>
        )}
      </Box>

      {/* Running progress bar */}
      {isRunning && runDoc && (
        <Box sx={{ flexShrink: 0, mb: 1 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
            <Typography
              variant="caption"
              color="info.main"
              sx={{ fontWeight: 600, fontSize: '0.7rem' }}
            >
              {lastAction || 'Working…'}
            </Typography>
            <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
              {currentIteration} / {maxIterations}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{ borderRadius: 1, height: 3 }}
            color="info"
          />
        </Box>
      )}

      {/* Step timeline */}
      <Box
        ref={scrollRef}
        onScroll={handleScroll}
        sx={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}
      >
        {hasSteps ? (
          steps!.map((step: any) => (
            <StepCard key={step.step} step={step} onBacktestHighlight={onBacktestHighlight} />
          ))
        ) : isRunning ? (
          <Box display="flex" alignItems="center" gap={1} sx={{ p: 1 }}>
            <CircularProgress size={14} color="info" />
            <Typography variant="body2" color="text.disabled" sx={{ fontSize: '0.75rem' }}>
              Starting up…
            </Typography>
          </Box>
        ) : fallbackResult ? (
          // Old sessions without steps subcollection
          <Box
            sx={{
              p: 1.5,
              borderRadius: 1,
              backgroundColor: theme.palette.background.custom3,
              border: `1px solid rgba(255,255,255,0.06)`,
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7, fontSize: '0.75rem' }}
            >
              {fallbackResult}
            </Typography>
          </Box>
        ) : (
          <Typography variant="body2" color="text.disabled" sx={{ fontSize: '0.75rem', p: 1 }}>
            No reasoning captured for this session.
          </Typography>
        )}
        <div ref={bottomRef} />
      </Box>

      {/* Message input — only when running */}
      {isRunning && (
        <Box
          sx={{
            flexShrink: 0,
            pt: 1,
            borderTop: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
          }}
        >
          {messageSent && (
            <Typography variant="caption" color="info.main" sx={{ fontSize: '0.65rem', px: 0.5 }}>
              Message sent — agent will see it on the next iteration
            </Typography>
          )}
          <Box display="flex" gap={1} alignItems="center">
            <TextField
              fullWidth
              size="small"
              placeholder="Send a message to the agent…"
              value={messageInput}
              onChange={(e) => setMessageInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  handleSendMessage()
                }
              }}
              disabled={isSending}
              sx={{ '& .MuiInputBase-input': { fontSize: '0.8rem' } }}
            />
            <IconButton
              color="primary"
              size="small"
              onClick={handleSendMessage}
              disabled={!messageInput.trim() || isSending}
            >
              {isSending ? <CircularProgress size={16} /> : <SendIcon fontSize="small" />}
            </IconButton>
          </Box>
        </Box>
      )}
    </Box>
  )
}

function formatToolName(name: string) {
  return name
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

function StepCard({
  step,
  onBacktestHighlight,
}: {
  step: any
  onBacktestHighlight: (id: string) => void
}) {
  const theme = useTheme()
  const { setActiveTab, usermeta } = useApplicationSuiteContext()

  const handleEntityClick = async (entityRef: { type: string; id: string }) => {
    if (entityRef.type === 'backtest') {
      onBacktestHighlight(entityRef.id)
      return
    }
    const snap = await getDoc(
      doc(firebase_firestore, `usermeta/${usermeta.gcid}/patterns/${entityRef.id}`)
    )
    if (!snap.exists()) return
    const data = { id: snap.id, ...snap.data() }
    setActiveTab({ tab: Tabs.PatternBuilder, options: { item: data } })
  }

  const formatTime = (ts: number) =>
    new Date(ts).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })

  if (step.type === 'user_message') {
    return (
      <Box
        sx={{
          p: 1.25,
          borderRadius: 1,
          backgroundColor: `${theme.palette.primary.main}10`,
          border: `1px solid rgba(255,255,255,0.06)`,
          borderLeft: `3px solid ${theme.palette.primary.main}`,
        }}
      >
        <Box display="flex" alignItems="center" gap={0.75} mb={0.5}>
          <PersonOutlineIcon sx={{ fontSize: 13, color: theme.palette.primary.main }} />
          <Typography
            variant="caption"
            color="primary.main"
            sx={{ fontWeight: 600, fontSize: '0.65rem' }}
          >
            You
          </Typography>
          <Typography
            variant="caption"
            color="text.disabled"
            sx={{ fontSize: '0.6rem', ml: 'auto' }}
          >
            {formatTime(step.timestamp)}
          </Typography>
        </Box>
        <Typography
          variant="body2"
          color="text.primary"
          sx={{ fontSize: '0.8rem', lineHeight: 1.5 }}
        >
          {step.content}
        </Typography>
      </Box>
    )
  }

  if (step.type === 'completion') {
    return (
      <Box
        sx={{
          p: 1.25,
          borderRadius: 1,
          backgroundColor: `${theme.palette.success.main}08`,
          border: `1px solid ${theme.palette.success.main}30`,
        }}
      >
        <Box display="flex" alignItems="center" gap={0.75} mb={step.reasoning ? 0.75 : 0}>
          <CheckCircleOutlineIcon sx={{ fontSize: 13, color: theme.palette.success.main }} />
          <Typography
            variant="caption"
            color="success.main"
            sx={{ fontWeight: 600, fontSize: '0.65rem' }}
          >
            Completed
          </Typography>
          <Typography
            variant="caption"
            color="text.disabled"
            sx={{ fontSize: '0.6rem', ml: 'auto' }}
          >
            {formatTime(step.timestamp)}
          </Typography>
        </Box>
        {step.reasoning && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: '0.78rem', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}
          >
            {step.reasoning}
          </Typography>
        )}
      </Box>
    )
  }

  // type: 'assistant'
  return (
    <Box
      sx={{
        p: 1.25,
        borderRadius: 1,
        backgroundColor: theme.palette.background.custom3,
        border: `1px solid rgba(255,255,255,0.06)`,
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        gap={0.75}
        mb={step.reasoning || step.toolCalls ? 0.75 : 0}
      >
        <SmartToyOutlinedIcon sx={{ fontSize: 13, color: theme.palette.text.secondary }} />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontWeight: 600, fontSize: '0.65rem' }}
        >
          Step {step.step}
        </Typography>
        <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.6rem', ml: 'auto' }}>
          {formatTime(step.timestamp)}
        </Typography>
      </Box>

      {step.reasoning && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            fontSize: '0.78rem',
            lineHeight: 1.6,
            mb: step.toolCalls?.length ? 0.75 : 0,
            whiteSpace: 'pre-wrap',
          }}
        >
          {step.reasoning}
        </Typography>
      )}

      {step.toolCalls?.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          {step.toolCalls.map((tc: any, i: number) => (
            <Box key={i} display="flex" alignItems="center" gap={0.5}>
              <Typography
                component="span"
                sx={{
                  fontSize: '0.65rem',
                  color: theme.palette.info.main,
                  fontFamily: "'JetBrains Mono', monospace",
                  flexShrink: 0,
                }}
              >
                [{formatToolName(tc.name)}]
              </Typography>
              <Typography
                component="span"
                sx={{ fontSize: '0.72rem', color: theme.palette.text.disabled }}
              >
                {tc.summary}
              </Typography>
              {tc.entityRef && (
                <Box sx={{ ml: 'auto', flexShrink: 0 }}>
                  <Tooltip
                    title={
                      tc.entityRef.name
                        ? tc.entityRef.version != null
                          ? `${tc.entityRef.name} v${tc.entityRef.version}`
                          : tc.entityRef.name
                        : tc.entityRef.id
                    }
                    placement="top"
                  >
                    <IconButton
                      size="small"
                      onClick={() => handleEntityClick(tc.entityRef)}
                      sx={{ p: 0.25, flexShrink: 0 }}
                    >
                      {tc.entityRef.type === 'backtest' ? (
                        <AssessmentIcon
                          sx={{ fontSize: 13, color: theme.palette.text.secondary }}
                        />
                      ) : (
                        <AccountTreeIcon
                          sx={{ fontSize: 13, color: theme.palette.text.secondary }}
                        />
                      )}
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  )
}

function RunningSessionCard({
  run,
  isSelected,
  onClick,
}: {
  run: any
  isSelected: boolean
  onClick: () => void
}) {
  const theme = useTheme()
  const progress =
    run.maxIterations > 0 ? Math.round((run.currentIteration / run.maxIterations) * 100) : 0
  const startedLabel = run.startedAt
    ? new Date(run.startedAt).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : null

  return (
    <Box
      onClick={onClick}
      sx={{
        p: 1.5,
        borderRadius: 1,
        cursor: 'pointer',
        border: `1px solid ${isSelected ? theme.palette.primary.main : theme.palette.info.dark}`,
        backgroundColor: isSelected
          ? `${theme.palette.primary.main}18`
          : `${theme.palette.info.main}0A`,
        transition: 'all 0.15s ease',
        '&:hover': {
          backgroundColor: isSelected
            ? `${theme.palette.primary.main}22`
            : theme.palette.action.hover,
        },
      }}
    >
      <Box display="flex" alignItems="center" gap={0.75} mb={0.75}>
        <SmartToyOutlinedIcon
          sx={{
            fontSize: 13,
            color: theme.palette.info.main,
            animation: 'agentPulse 1.5s ease-in-out infinite',
            '@keyframes agentPulse': {
              '0%, 100%': { opacity: 1 },
              '50%': { opacity: 0.3 },
            },
          }}
        />
        <Typography
          variant="caption"
          color="info.main"
          sx={{ fontSize: '0.65rem', fontWeight: 600 }}
        >
          Running
        </Typography>
      </Box>

      <Typography
        variant="body2"
        color="text.secondary"
        sx={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          mb: 0.75,
          fontSize: '0.75rem',
        }}
      >
        {run.lastAction || 'Starting…'}
      </Typography>

      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{ borderRadius: 1, height: 3, mb: 0.5 }}
        color="info"
      />
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.6rem' }}>
          {run.currentIteration} / {run.maxIterations} iterations
        </Typography>
        {startedLabel && (
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.6rem' }}>
            {startedLabel}
          </Typography>
        )}
      </Box>
    </Box>
  )
}

const App = () => <Application AppComponent={SessionsContent} />

export default App
